import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTheme } from '../hooks/useTheme';
import {
  APP_DOWNLOAD_LINKS,
  shouldShowBrowserAppLinks,
} from '../utilities/desktopAppHandoff';
import { isElectron, isWeb } from '../utilities/platform';
import Icon from './Icon';

interface Props {
  expanded: boolean;
  onRequestExpand: () => void;
}

export default function AppDownloadMenu({ expanded, onRequestExpand }: Props) {
  const theme = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!expanded) setOpen(false);
  }, [expanded]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (!shouldShowBrowserAppLinks(isWeb, isElectron)) return null;

  const visit = (url: string) => {
    setOpen(false);
    if (typeof window !== 'undefined') window.location.assign(url);
  };

  return (
    <View style={styles.wrap}>
      {open && expanded && (
        <View
          style={styles.menu}
          accessibilityRole="menu"
          accessibilityLabel="Download Eleplan apps"
        >
          <TouchableOpacity
            style={styles.menuRow}
            onPress={() => visit(APP_DOWNLOAD_LINKS.desktop)}
            accessibilityRole="link"
            accessibilityLabel="Get Eleplan desktop"
          >
            <Icon icon={['fas', 'desktop']} size={17} color={theme.colors.text} />
            <Text style={styles.menuLabel}>Get Eleplan desktop</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.menuRow}
            onPress={() => visit(APP_DOWNLOAD_LINKS.mobile)}
            accessibilityRole="link"
            accessibilityLabel="Get Eleplan mobile"
          >
            <Icon icon={['fas', 'mobile']} size={18} color={theme.colors.text} />
            <Text style={styles.menuLabel}>Get Eleplan mobile</Text>
          </TouchableOpacity>
        </View>
      )}

      <TouchableOpacity
        style={expanded ? styles.trigger : styles.triggerCollapsed}
        onPress={() => {
          onRequestExpand();
          setOpen((value) => !value);
        }}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel="Get Eleplan apps"
        accessibilityState={{ expanded: open }}
      >
        <Icon icon={['fas', 'desktop']} size={22} color={theme.colors.muted} />
        {expanded && <Text style={styles.triggerLabel}>Get Eleplan apps</Text>}
      </TouchableOpacity>
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof import('../hooks/useTheme').useTheme>) {
  return StyleSheet.create({
    wrap: {
      position: 'relative',
      zIndex: 60,
    },
    trigger: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 11,
      paddingHorizontal: 14,
      paddingVertical: 10,
      borderRadius: 11,
      marginBottom: 2,
    },
    triggerCollapsed: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 10,
      borderRadius: 11,
      marginBottom: 2,
    },
    triggerLabel: {
      color: theme.colors.text,
      fontSize: 14,
      fontWeight: '500',
      flex: 1,
    },
    menu: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 50,
      padding: 6,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: theme.colors.border,
      borderRadius: 13,
      backgroundColor: theme.colors.card,
      shadowColor: '#000',
      shadowOpacity: 0.18,
      shadowRadius: 18,
      shadowOffset: { width: 0, height: 8 },
      elevation: 12,
    },
    menuRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingHorizontal: 10,
      paddingVertical: 10,
      borderRadius: 9,
    },
    menuLabel: {
      color: theme.colors.text,
      fontSize: 13,
      fontWeight: '500',
    },
  });
}
